import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CircleHelp, Plus } from 'lucide-react';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import SheinSyncMaxPagesModal from '@/shein-platform/components/SheinSyncMaxPagesModal';
import DiscussAutoRuleModal from '@/shein-platform/modules/discuss/DiscussAutoRuleModal';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import BatchQueryModal from '@/shein-platform/modules/products/components/BatchQueryModal';
import {
  joinBatchTokens,
  parseBatchTokens,
} from '@/shein-platform/modules/products/components/batchQueryUtils';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
/* eslint-disable no-restricted-imports -- 同包相对引用，避免别名解析导致 no-undef */
import {
  useSheinPageShopScope,
  stackShopDimensionTabs,
} from '../../hooks/useSheinPageShopScope';
/* eslint-enable no-restricted-imports */
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import {
  DISCUSS_STATUS_LABELS,
  DISCUSS_TYPE_LABELS,
} from '@/shein-platform/constants/sheinApiCatalog';
import { sheinShopCapabilities } from '@/shein-platform/utils/sheinShopCapabilities';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import {
  discussStatusLabel,
  discussStatusModifier,
  discussTypeLabel,
  extractDiscussList,
  extractDiscussTotal,
  formatCostMoney,
  formatSkuPricesSummary,
  formatSkuQuoteMoney,
  formatSkuSuggestMoney,
  latestCostHistory,
  normalizeDiscussImageUrl,
} from '@/shein-platform/modules/discuss/discussDisplay';
import { rememberDiscussPendingForHome } from '@/shein-platform/utils/sheinHomeLocalSnapshot';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import { showToast } from '@/utils/toast';
import '@/styles/shein-platform/shein-product-ops.css';

const STATUS_TABS = [
  { value: '', label: '全部' },
  ...Object.entries(DISCUSS_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

const TYPE_FILTER_OPTIONS = [
  { value: '', label: '全部类型' },
  ...Object.entries(DISCUSS_TYPE_LABELS).map(([value, label]) => ({ value: String(value), label })),
];

function ProcessDiscussModal({
  open,
  row,
  busy,
  initialMode = 'requote',
  onClose,
  onSubmit,
}) {
  const mode = initialMode || 'requote';
  const [reason, setReason] = useState('');
  const [skuPrices, setSkuPrices] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open || !row) return;
    setReason('');
    setFiles([]);
    setUploading(false);
    setDragOver(false);
    const skus = Array.isArray(row.skuCostPrices) ? row.skuCostPrices : [];
    setSkuPrices(skus.map((sku) => {
      const last = latestCostHistory(sku);
      const suggest = sku?.suggestCostPrice;
      return {
        skuCode: sku?.skuCode || '',
        saleAttributeValues: Array.isArray(sku?.saleAttributeValues) ? sku.saleAttributeValues : [],
        cost: suggest != null && suggest !== '' ? String(suggest) : (last?.costPrice != null ? String(last.costPrice) : ''),
        currency: sku?.suggestCostCurrency || last?.currency || '',
        lastCost: last?.costPrice,
        lastCurrency: last?.currency || '',
      };
    }));
  }, [open, row, initialMode]);

  if (!open || !row) return null;

  const submit = () => {
    if (mode === 'accept' || mode === 'reject') {
      onSubmit({
        confirmInfos: [{
          discussAuditType: mode === 'accept' ? '1' : '2',
          discussSn: row.discussSn,
        }],
      });
      return;
    }
    const skuCostInfoList = skuPrices.map((s) => ({
      skuCode: s.skuCode,
      cost: Number(s.cost),
      currency: s.currency,
      lastCost: Number(s.lastCost),
      lastCurrency: s.lastCurrency,
    }));
    for (const item of skuCostInfoList) {
      if (!item.skuCode || !Number.isFinite(item.cost) || !item.currency
        || !Number.isFinite(item.lastCost) || !item.lastCurrency) {
        showToast(fixedUi("请完善每个 SKU 的报价与币种"), 'warning');
        return;
      }
    }
    if (Number(row.isSizeSamePrice) === 1) {
      const first = skuCostInfoList[0]?.cost;
      if (skuCostInfoList.some((s) => s.cost !== first)) {
        showToast(fixedUi("该 SKC 要求所有尺码同价，请填写相同成本价"), 'warning');
        return;
      }
    }
    onSubmit({
      createCostDiscusses: [{
        discussSn: row.discussSn,
        discussStep: Number(row.serialNumber) || 1,
        reason: reason.trim() || undefined,
        skcName: row.skcName,
        fileUploadList: files.length ? files : undefined,
        skuCostInfoList,
      }],
    });
  };

  const uploadPickedFiles = async (fileList) => {
    const picked = Array.from(fileList || []).filter((f) => f && String(f.type || '').startsWith('image/'));
    if (!picked.length) {
      showToast(fixedUi("请选择图片文件"), 'warning');
      return;
    }
    if (!row.__shopId) {
      showToast(fixedUi("缺少店铺信息，无法上传"), 'error');
      return;
    }
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of picked) {
        const res = await sheinPlatformAPI.uploadDiscussFile(row.__shopId, file, 1);
        const objectKey = res?.objectKey || res?.object_key;
        const url = res?.url;
        if (!objectKey || !url) {
          throw new Error(fixedUi('上传未返回 objectKey/url'));
        }
        uploaded.push({
          file_name: file.name,
          object_key: objectKey,
          type: 1,
          url,
        });
      }
      setFiles((prev) => [...prev, ...uploaded]);
      showToast(fixedUiTpl('已上传 {0} 个文件', uploaded.length), 'success');
    } catch (err) {
      showToast(err?.message || fixedUi("上传失败"), 'error');
    } finally {
      setUploading(false);
    }
  };

  const onPickFiles = async (e) => {
    const list = e.target.files;
    e.target.value = '';
    await uploadPickedFiles(list);
  };

  const removeFile = (objectKey) => {
    setFiles((prev) => prev.filter((f) => f.object_key !== objectKey));
  };

  const title = mode === 'requote'
    ? fixedUi('重新报价')
    : mode === 'accept'
      ? fixedUi("同意平台建议价")
      : fixedUi('拒绝议价');

  const productImg = normalizeDiscussImageUrl(row.mainPicUrl);

  return (
    <div className="spo-batch-modal" role="presentation">
      <div className="spo-batch-modal__backdrop" aria-hidden="true" />
      <div
        className="spo-batch-modal__panel spo-discuss-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-discuss-process-title"
      >
        <header className="spo-batch-modal__head">
          <h2 id="spo-discuss-process-title">{title}</h2>
          <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onClose}>{fixedUi("关闭")}</button>
        </header>
        <div className="spo-batch-modal__body">
          {mode === 'requote' ? (
            <div className="spo-discuss-requote">
              {/* 第一行：产品信息（与列表表头产品信息布局一致） */}
              <section className="spo-discuss-requote__row" aria-label={fixedUi("产品信息")}>
                <div className="spo-discuss-requote__row-label">{fixedUi("产品信息")}</div>
                <div className="spo-return-product spo-discuss-requote__product-cell">
                  {productImg ? (
                    <SheinProductThumb
                      imageUrl={productImg}
                      className="spo-return-product__img"
                      placeholderClassName="spo-return-product__img spo-return-product__img--empty"
                      alt={row.productTitle || fixedUi('议价商品')}
                    />
                  ) : (
                    <div className="spo-return-product__img spo-return-product__img--empty" />
                  )}
                  <div className="spo-return-product__meta">
                    <HoverCopyField
                      value={row.productTitle || ''}
                      className="spo-return-product__title"
                      popoverCopy
                    />
                    <HoverCopyField
                      label="SPU"
                      value={row.spuName || row.spu_name || ''}
                      className="spo-return-product__sku"
                    />
                    <HoverCopyField
                      label="SKC"
                      value={row.skcName || ''}
                      className="spo-return-product__sku"
                    />
                    <HoverCopyField
                      label={fixedUi("货号")}
                      value={row.supplierCode || ''}
                      className="spo-return-product__sku"
                    />
                    <div className="spo-return-product__sku">{row.saleAttributeValue || '—'}</div>
                  </div>
                </div>
              </section>

              {/* 第二行：报价与修改价格（不展示 SKU） */}
              <section className="spo-discuss-requote__row" aria-label={fixedUi("报价信息")}>
                <div className="spo-discuss-requote__row-label">{fixedUi("报价信息")}</div>
                <div className="spo-discuss-requote__prices">
                  {skuPrices.map((s, idx) => {
                    const raw = (row.skuCostPrices || [])[idx] || {};
                    return (
                      <div key={s.skuCode || idx} className="spo-discuss-requote__price-card">
                        <div className="spo-discuss-requote__price-grid">
                          <div className="spo-discuss-requote__price-item">
                            <span>{fixedUi("上次报价")}</span>
                            <strong>{formatCostMoney(s.lastCost, s.lastCurrency)}</strong>
                          </div>
                          <div className="spo-discuss-requote__price-item">
                            <span>{fixedUi("平台建议价")}</span>
                            <strong>{formatCostMoney(raw.suggestCostPrice, raw.suggestCostCurrency)}</strong>
                          </div>
                          <label className="spo-discuss-requote__price-item spo-discuss-requote__price-item--edit">
                            <span>{fixedUi("本次报价")}</span>
                            <input
                              className="spo-input"
                              value={s.cost}
                              disabled={busy || uploading}
                              onChange={(e) => {
                                const v = e.target.value;
                                setSkuPrices((prev) => prev.map((item, i) => (i === idx ? { ...item, cost: v } : item)));
                              }}
                            />
                          </label>
                          <label className="spo-discuss-requote__price-item spo-discuss-requote__price-item--edit">
                            <span>{fixedUi("币种")}</span>
                            <input
                              className="spo-input"
                              value={s.currency}
                              disabled={busy || uploading}
                              onChange={(e) => {
                                const v = e.target.value;
                                setSkuPrices((prev) => prev.map((item, i) => (i === idx ? { ...item, currency: v } : item)));
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* 第三行：议价理由 */}
              <section className="spo-discuss-requote__row" aria-label={fixedUi("议价理由")}>
                <div className="spo-discuss-requote__row-label">{fixedUi("议价理由")}</div>
                <label className="spo-discuss-field spo-discuss-field--flush">
                  <span className="spo-discuss-requote__optional">{fixedUi("可选，最多 255 字")}</span>
                  <textarea
                    className="spo-batch-modal__textarea"
                    value={reason}
                    maxLength={255}
                    rows={3}
                    disabled={busy || uploading}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={fixedUi("说明重新报价原因")}
                  />
                </label>
              </section>

              {/* 第四行：辅助材料上传通道 */}
              <section className="spo-discuss-requote__row" aria-label={fixedUi("辅助材料")}>
                <div className="spo-discuss-requote__row-label">{fixedUi("辅助材料")}</div>
                <div className="spo-discuss-upload">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    disabled={busy || uploading}
                    onChange={onPickFiles}
                  />
                  <button
                    type="button"
                    className={[
                      'spo-discuss-upload__drop',
                      dragOver ? 'spo-discuss-upload__drop--over' : '',
                      uploading ? 'spo-discuss-upload__drop--busy' : '',
                    ].filter(Boolean).join(' ')}
                    disabled={busy || uploading}
                    aria-busy={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!busy && !uploading) setDragOver(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!busy && !uploading) setDragOver(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOver(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOver(false);
                      if (busy || uploading) return;
                      uploadPickedFiles(e.dataTransfer?.files);
                    }}
                  >
                    {uploading ? (
                      <span className="spo-discuss-upload__busy">{fixedUi("上传中…")}</span>
                    ) : (
                      <>
                        <span className="spo-discuss-upload__plus" aria-hidden="true">+</span>
                        <span className="spo-discuss-upload__title">
                          {dragOver ? fixedUi('松开上传图片') : fixedUi('点击或拖拽上传辅助材料')}
                        </span>
                        <span className="spo-discuss-upload__hint">{fixedUi("支持图片，可多选")}</span>
                      </>
                    )}
                  </button>
                  {files.length ? (
                    <ul className="spo-discuss-upload__files">
                      {files.map((f) => (
                        <li key={f.object_key}>
                          <a href={f.url} target="_blank" rel="noopener noreferrer">{f.file_name || f.object_key}</a>
                          <button
                            type="button"
                            className="spo-btn spo-btn--ghost spo-btn--sm"
                            disabled={busy || uploading}
                            onClick={() => removeFile(f.object_key)}
                          >
                            {fixedUi("移除")}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </section>
            </div>
          ) : (
            <p className="spo-batch-modal__hint">
              {mode === 'accept'
                ? fixedUi('同意后将提交 confirmInfos（discussAuditType=1），商品价格变为平台建议价。')
                : fixedUi('拒绝后将提交 confirmInfos（discussAuditType=2），议价单结束且无法再报价上架。')}
            </p>
          )}
        </div>
        <footer className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--secondary" disabled={busy || uploading} onClick={onClose}>{fixedUi("取消")}</button>
          <button type="button" className="spo-btn spo-btn--primary" disabled={busy || uploading} onClick={submit}>
            {busy ? fixedUi("提交中…") : fixedUi('确认提交')}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function DiscussListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    activeShop,
    activeShopId,
    loading: shopLoading,
    concreteScopeId,
    allShopsScope,
    shopDimensionTabs,
  } = useSheinPageShopScope();
  const shopId = concreteScopeId || (allShopsScope ? '' : (activeShop?.id || activeShopId));
  const caps = sheinShopCapabilities(activeShop);
  const canDiscuss = Boolean(caps.canDiscussCost);
  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();

  const initialStatus = searchParams.get('status') || '';
  const [status, setStatus] = useState(initialStatus);
  const [filterDraft, setFilterDraft] = useState({
    discussSn: '',
    skcName: '',
    supplierCode: '',
    discussType: '',
  });
  const [query, setQuery] = useState({
    discussSn: '',
    skcName: '',
    supplierCode: '',
    discussType: '',
    _nonce: 0,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState(() => ({}));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processRow, setProcessRow] = useState(null);
  const [processMode, setProcessMode] = useState('requote');
  const [processing, setProcessing] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [batchModalField, setBatchModalField] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [autoRuleModalOpen, setAutoRuleModalOpen] = useState(false);

  useEffect(() => {
    const next = searchParams.get('status') || '';
    setStatus(next);
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
    setRows([]);
    setTotal(0);
    setError('');
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
    setLastSyncedAt('');
    setPersistedSyncedAt('');
  }, [shopId]);

  useEffect(() => {
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, [query._nonce, status]);

  useEffect(() => {
    if (!shopId || !canDiscuss) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getShopSyncStatus(shopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const mod = modules.find((m) => String(m.module || '').toUpperCase() === 'DISCUSS');
        const at = mod?.lastSyncEndAt || mod?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [shopId, canDiscuss]);

  const load = useCallback(async () => {
    if (!shopId || !canDiscuss) {
      setRows([]);
      setTotal(0);
      setStatusCounts({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const body = {
        source: 'cached',
        pageNum: page,
        pageSize,
        discussSnList: parseBatchTokens(query.discussSn),
        skcNameList: parseBatchTokens(query.skcName),
        supplierCodeList: parseBatchTokens(query.supplierCode),
      };
      if (status !== '' && status != null) {
        body.discussStatus = Number(status);
      }
      if (query.discussType !== '' && query.discussType != null) {
        body.discussType = Number(query.discussType);
      }
      const res = await sheinPlatformAPI.listDiscussOrders(shopId, body);
      const list = extractDiscussList(res);
      const totalCount = extractDiscussTotal(res, list.length);
      setRows(list);
      setTotal(totalCount);
      const counts = res?.statusCounts && typeof res.statusCounts === 'object' ? res.statusCounts : {};
      setStatusCounts(counts);
      if (String(status) === '1') {
        rememberDiscussPendingForHome(shopId, totalCount);
      } else {
        const pending = Number(counts?.['1'] ?? counts?.[1]);
        if (Number.isFinite(pending) && pending >= 0) {
          rememberDiscussPendingForHome(shopId, pending);
        }
      }
    } catch (e) {
      setRows([]);
      setTotal(0);
      setStatusCounts({});
      setError(e?.message || fixedUi('加载议价单失败'));
    } finally {
      setLoading(false);
    }
  }, [shopId, canDiscuss, page, pageSize, status, query]);

  useEffect(() => {
    if (shopLoading) return;
    load();
  }, [load, shopLoading]);

  const handleSync = useCallback(async (options = {}) => {
    if (!shopId || !canDiscuss) {
      showToast(fixedUi("请先选择可用店铺"), 'warning');
      return;
    }
    try {
      const track = await startJob({
        kind: 'discuss',
        shopId,
        get label() { return fixedUi("同步议价单"); },
        payload: {
          maxPages: options?.maxPages,
        },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      const finishedAt = new Date().toISOString();
      setLastSyncedAt(finishedAt);
      setPersistedSyncedAt(finishedAt);
      setQuery((prev) => ({ ...prev, _nonce: prev._nonce + 1 }));
      setPage(1);
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      if (e?.message) showToast(e.message, 'error');
      throw e;
    }
  }, [shopId, canDiscuss, startJob, onTrackDone]);

  const { refresh: guardedSync, refreshing: syncingLocal, cooldownSeconds: syncCooldown } = useGuardedRefresh(
    handleSync,
    { minIntervalMs: 3000 },
  );
  const syncing = syncingLocal || isBusy('discuss', shopId);
  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const lastSyncedLabel = syncing
    ? fixedUi("同步中…")
    : (latestSyncedAt
      ? `${fixedUi('最近同步')}：${formatSiteListSyncedAt(latestSyncedAt)}`
      : fixedUi('尚未同步，请点击「拉取 SHEIN 后台数据」'));

  const listTabs = useMemo(() => stackShopDimensionTabs(shopDimensionTabs, (
    <div className="spo-status-tabs" role="tablist" aria-label={fixedUi("议价单状态")}>
      {STATUS_TABS.map((tab) => {
        const countKey = tab.value === '' ? 'all' : String(tab.value);
        const count = Number(statusCounts?.[countKey] ?? 0);
        return (
          <button
            key={tab.value || 'all'}
            type="button"
            role="tab"
            aria-selected={String(status) === String(tab.value)}
            className={`spo-status-tab${String(status) === String(tab.value) ? ' spo-status-tab--active' : ''}`}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              if (tab.value === '') next.delete('status');
              else next.set('status', tab.value);
              setSearchParams(next, { replace: true });
              setPage(1);
            }}
          >
            {fixedUi(tab.label)}
            <span className="spo-status-tab__count">{Number.isFinite(count) ? count : 0}</span>
          </button>
        );
      })}
    </div>
  )), [shopDimensionTabs, status, searchParams, setSearchParams, statusCounts]);

  const rowKey = (row) => String(row.discussSn || `${row.skcName}-${row.serialNumber}`);
  const selectableKeys = useMemo(
    () => rows.map(rowKey).filter(Boolean),
    [rows],
  );
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selectedKeys.has(k));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key, row) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        if (row) selectedRowsRef.current.set(key, row);
      }
      return next;
    });
    if (row && !selectedRowsRef.current.has(key)) {
      selectedRowsRef.current.set(key, row);
    }
  };

  useEffect(() => {
    rows.forEach((row) => {
      const key = rowKey(row);
      if (selectedKeys.has(key)) selectedRowsRef.current.set(key, row);
    });
  }, [rows, selectedKeys]);

  const onQuery = () => {
    setQuery({
      discussSn: filterDraft.discussSn,
      skcName: filterDraft.skcName,
      supplierCode: filterDraft.supplierCode,
      discussType: filterDraft.discussType,
      _nonce: query._nonce + 1,
    });
    setPage(1);
  };

  const onReset = () => {
    setFilterDraft({ discussSn: '', skcName: '', supplierCode: '', discussType: '' });
    setQuery({ discussSn: '', skcName: '', supplierCode: '', discussType: '', _nonce: query._nonce + 1 });
    setPage(1);
  };

  const batchModalMeta = {
    discussSn: { title: fixedUi('批量查询议价单号'), placeholder: fixedUi('每行一个议价单号，或用逗号分隔') },
    skcName: { title: fixedUi('批量查询 SKC'), placeholder: fixedUi('每行一个平台 SKC，或用逗号分隔') },
    supplierCode: { title: fixedUi('批量查询货号'), placeholder: fixedUi('每行一个商家货号，或用逗号分隔') },
  }[batchModalField] || { get title() { return fixedUi("批量查询"); }, placeholder: fixedUi('每行一条，或用逗号分隔') };

  const openProcessModal = (row, mode) => {
    setProcessMode(mode);
    setProcessRow({ ...row, __shopId: shopId });
  };

  const submitConfirmAction = async (row, auditType) => {
    if (!shopId || !row?.discussSn) return;
    const isAccept = String(auditType) === '1';
    const ok = await showSheinConfirm({
      title: isAccept ? fixedUi("同意平台建议价") : fixedUi('拒绝议价'),
      message: isAccept
        ? fixedUiTpl('确认同意议价单「{0}」的平台建议价？将提交 confirmInfos（discussAuditType=1）。', row.discussSn)
        : fixedUiTpl('确认拒绝议价单「{0}」？将提交 confirmInfos（discussAuditType=2），拒绝后无法再报价上架。', row.discussSn),
      tone: isAccept ? 'info' : 'danger',
      confirmText: isAccept ? fixedUi('确认同意') : fixedUi('确认拒绝'),
      cancelText: fixedUi('取消'),
    });
    if (!ok) return;
    setProcessing(true);
    try {
      const res = await sheinPlatformAPI.processDiscussOrder(shopId, {
        confirmInfos: [{
          discussAuditType: String(auditType),
          discussSn: row.discussSn,
        }],
      });
      const success = Number(res?.successCount) || 0;
      const fail = Number(res?.failCount) || 0;
      if (fail > 0 && success === 0) {
        showToast(res?.msg || fixedUi('处理失败'), 'error');
      } else {
        showToast(success ? fixedUiTpl('处理成功 {0} 单', success) : (res?.msg || fixedUi('已提交')), 'success');
        await load();
      }
    } catch (e) {
      showToast(e?.message || fixedUi('处理议价单失败'), 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleProcess = async (payload) => {
    if (!shopId || !processRow) return;
    setProcessing(true);
    try {
      const res = await sheinPlatformAPI.processDiscussOrder(shopId, payload);
      const ok = Number(res?.successCount) || 0;
      const fail = Number(res?.failCount) || 0;
      if (fail > 0 && ok === 0) {
        showToast(res?.msg || fixedUi('处理失败'), 'error');
      } else {
        showToast(ok ? fixedUiTpl('处理成功 {0} 单', ok) : (res?.msg || fixedUi('已提交')), 'success');
        setProcessRow(null);
        await load();
      }
    } catch (e) {
      showToast(e?.message || fixedUi('处理议价单失败'), 'error');
    } finally {
      setProcessing(false);
    }
  };

  const batchConfirm = async (auditType) => {
    if (!shopId || selectedKeys.size === 0) return;
    const pending = [];
    selectedKeys.forEach((key) => {
      const row = selectedRowsRef.current.get(key) || rows.find((r) => rowKey(r) === key);
      if (row && Number(row.discussStatus) === 1 && row.discussSn) {
        pending.push({
          discussAuditType: String(auditType),
          discussSn: row.discussSn,
        });
      }
    });
    if (!pending.length) {
      showToast(fixedUi("请选择待商家确认的议价单"), 'warning');
      return;
    }
    setBatchBusy(true);
    try {
      const res = await sheinPlatformAPI.processDiscussOrder(shopId, { confirmInfos: pending });
      const ok = Number(res?.successCount) || 0;
      const fail = Number(res?.failCount) || 0;
      if (fail > 0 && ok === 0) {
        showToast(res?.msg || fixedUi('批量处理失败'), 'error');
      } else {
        showToast(fixedUiTpl('已处理 {0} 单{1}', ok || pending.length, fail ? fixedUiTpl('，失败 {0}', fail) : ''), 'success');
        setSelectedKeys(new Set());
        selectedRowsRef.current.clear();
        await load();
      }
    } catch (e) {
      showToast(e?.message || fixedUi('批量处理失败'), 'error');
    } finally {
      setBatchBusy(false);
    }
  };

  const exportRows = (source) => {
    if (!source.length) {
      showToast(fixedUi("暂无可导出数据"), 'warning');
      return;
    }
    downloadCsv(
      `shein-discuss-${Date.now()}.csv`,
      ['discussSn', 'spuName', 'skcName', 'supplierCode', 'discussType', 'discussStatus', 'serialNumber', 'appealCount'],
      source.map((row) => ({
        discussSn: row.discussSn || '',
        spuName: row.spuName || row.spu_name || '',
        skcName: row.skcName || '',
        supplierCode: row.supplierCode || '',
        discussType: discussTypeLabel(row.discussType),
        discussStatus: discussStatusLabel(row.discussStatus),
        serialNumber: row.serialNumber ?? '',
        appealCount: row.appealCount ?? '',
      })),
    );
    showToast(fixedUiTpl('已导出 {0} 条', source.length), 'success');
  };

  const exportFiltered = async () => {
    if (!shopId || !canDiscuss) {
      showToast(fixedUi("请先选择可用店铺"), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllPages(async (pageNo, size) => {
        const body = {
          source: 'cached',
          pageNum: pageNo,
          pageSize: size,
          discussSnList: parseBatchTokens(query.discussSn),
          skcNameList: parseBatchTokens(query.skcName),
          supplierCodeList: parseBatchTokens(query.supplierCode),
        };
        if (status !== '' && status != null) {
          body.discussStatus = Number(status);
        }
        if (query.discussType !== '' && query.discussType != null) {
          body.discussType = Number(query.discussType);
        }
        const res = await sheinPlatformAPI.listDiscussOrders(shopId, body);
        const list = extractDiscussList(res);
        return { items: list, total: extractDiscussTotal(res, list.length) };
      });
      exportRows(all);
    } catch (e) {
      showToast(e?.message || fixedUi("导出筛选失败"), 'error');
    } finally {
      setExporting(false);
    }
  };

  if (!shopLoading && shopId && !canDiscuss) {
    return (
      <ProductListCard>
        <p className="spo-empty-hint">
          {fixedUi('当前为自运营店铺，成本议价单不适用。请从侧栏进入「价格处理 → 建议零售价」。')}
        </p>
      </ProductListCard>
    );
  }

  const colSpan = 9;

  return (
    <>
      <SheinSyncProgressSlot kinds={['discuss']} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || processing || batchBusy || syncing || exporting}
            syncKinds={['discuss']}
            onSync={() => {
              if (!shopId || !canDiscuss) {
                showToast(fixedUi("请先选择可用店铺"), 'warning');
                return;
              }
              setSyncModalOpen(true);
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing || !canDiscuss}
            lastSyncedLabel={lastSyncedLabel}
            syncHint={fixedUi('从 SHEIN 后台拉取议价单，更新到 IPE 本地。')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting || (!rows.length && selectedKeys.size === 0 && total <= 0)}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: () => exportRows(rows),
              onExportSelected: () => exportRows(
                [...selectedKeys].map((k) => selectedRowsRef.current.get(k)).filter(Boolean),
              ),
            })}
            extraActions={[
              {
                key: 'auto-rules',
                get label() { return fixedUi("自动规则"); },
                disabled: !shopId || !canDiscuss || loading || processing || batchBusy || syncing,
                onClick: () => setAutoRuleModalOpen(true),
              },
              {
                key: 'batch-accept',
                label: batchBusy ? fixedUi('处理中…') : fixedUi('批量同意'),
                tone: 'primary',
                disabled: selectedKeys.size === 0 || batchBusy,
                onClick: () => batchConfirm(1),
              },
              {
                key: 'batch-reject',
                label: fixedUi('批量拒绝'),
                tone: 'danger',
                disabled: selectedKeys.size === 0 || batchBusy,
                onClick: () => batchConfirm(2),
              },
            ]}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline spo-field--batch spo-field--w300">
              <span className="spo-field__label">{fixedUi("议价单号")}</span>
              <input
                className="spo-input"
                value={filterDraft.discussSn}
                disabled={!shopId || !canDiscuss}
                placeholder={fixedUi("支持批量，逗号分隔")}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, discussSn: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={fixedUi("批量输入议价单号")}
                title={fixedUi("批量查询")}
                disabled={!shopId || !canDiscuss}
                onClick={() => setBatchModalField('discussSn')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="spo-field spo-field--inline spo-field--batch spo-field--w300">
              <span className="spo-field__label">SKC</span>
              <input
                className="spo-input"
                value={filterDraft.skcName}
                disabled={!shopId || !canDiscuss}
                placeholder={fixedUi("平台 SKC，支持批量")}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, skcName: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={fixedUi("批量输入 SKC")}
                title={fixedUi("批量查询")}
                disabled={!shopId || !canDiscuss}
                onClick={() => setBatchModalField('skcName')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="spo-field spo-field--inline spo-field--batch spo-field--w300">
              <span className="spo-field__label">{fixedUi("货号")}</span>
              <input
                className="spo-input"
                value={filterDraft.supplierCode}
                disabled={!shopId || !canDiscuss}
                placeholder={fixedUi("商家货号，支持批量")}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, supplierCode: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={fixedUi("批量输入货号")}
                title={fixedUi("批量查询")}
                disabled={!shopId || !canDiscuss}
                onClick={() => setBatchModalField('supplierCode')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="spo-field spo-field--inline">
              <span className="spo-field__label">{fixedUi("类型")}</span>
              <select
                className="spo-select"
                value={filterDraft.discussType}
                disabled={!shopId || !canDiscuss}
                aria-label={fixedUi("议价单类型")}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, discussType: e.target.value }))}
              >
                {TYPE_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="spo-filter-bar__actions">
            <button
              type="button"
              className="spo-btn spo-btn--secondary spo-btn--sm"
              disabled={!shopId || !canDiscuss}
              onClick={onReset}
            >
              {fixedUi("重置")}
            </button>
            <button
              type="button"
              className="spo-btn spo-btn--primary spo-btn--sm"
              disabled={!shopId || loading || !canDiscuss}
              onClick={onQuery}
            >
              {fixedUi("查询")}
            </button>
          </div>
        </div>
        {error ? <div className="spo-ops-error" role="alert">{error}</div> : null}
      </ProductFilterCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalMeta.title}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalMeta.placeholder}
        onConfirm={(tokens) => {
          if (!batchModalField) return;
          setFilterDraft((prev) => ({ ...prev, [batchModalField]: joinBatchTokens(tokens) }));
        }}
        onClose={() => setBatchModalField(null)}
      />

      <ProductListCard
        tabs={listTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={fixedUi('正在查询议价单…')}
      >
        <div className="spo-drafts__table-wrap spo-ops-table-wrap">
          <table className="spo-publish__table spo-drafts__table spo-ops-table spo-ops-table--price">
            <colgroup>
              <col className="spo-ops-col spo-ops-col--check shein-product-col--check" />
              <col className="spo-ops-col spo-ops-col--absorb" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--ops spo-ops-col--share" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="spo-ops-th spo-ops-th--check shein-product-cell--spu-check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={fixedUi("全选议价单")}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col">{fixedUi("产品信息")}</th>
                <th scope="col">{fixedUi("议价单号")}</th>
                <th scope="col">{fixedUi("类型")}</th>
                <th scope="col">{fixedUi("报价")}</th>
                <th scope="col">{fixedUi("状态")}</th>
                <th scope="col">{fixedUi("轮次/剩余")}</th>
                <th scope="col">{fixedUi("平台建议价")}</th>
                <th scope="col" className="spo-ops-th--ops">{fixedUi("操作")}</th>
              </tr>
            </thead>
            <tbody>
              {!shopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="spo-certs-empty-cell">
                    <div className="spo-certs-empty">
                      <p className="spo-certs-empty__title">
                        {!shopId ? (allShopsScope ? '请选择具体店铺查看' : fixedUi("请先选择店铺")) : loading ? fixedUi("加载中…") : '暂无议价单'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const key = rowKey(row);
                  const img = normalizeDiscussImageUrl(row.mainPicUrl);
                  const skus = Array.isArray(row.skuCostPrices) ? row.skuCostPrices : [];
                  const quoteText = formatSkuPricesSummary(skus, formatSkuQuoteMoney);
                  const suggestText = formatSkuPricesSummary(skus, formatSkuSuggestMoney);
                  const appealReason = String(row.appealReason || row.appeal_reason || '').trim();
                  const canProcess = Number(row.discussStatus) === 1;
                  return (
                    <tr key={key}>
                      <td
                        className="spo-ops-td spo-ops-td--check shein-product-cell--spu-check"
                        onClick={() => toggleOne(key, row)}
                      >
                        <input
                          type="checkbox"
                          aria-label={fixedUiTpl('选择 {0}', row.discussSn || key)}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key, row)}
                        />
                      </td>
                      <td className="spo-ops-td spo-ops-td--product">
                        <div className="spo-return-product">
                          {img ? (
                            <SheinProductThumb
                              imageUrl={img}
                              className="spo-return-product__img"
                              placeholderClassName="spo-return-product__img spo-return-product__img--empty"
                              alt={row.productTitle || fixedUi('议价商品')}
                            />
                          ) : (
                            <div className="spo-return-product__img spo-return-product__img--empty" />
                          )}
                          <div className="spo-return-product__meta">
                            <HoverCopyField
                              value={row.productTitle || ''}
                              className="spo-return-product__title"
                              popoverCopy
                            />
                            <HoverCopyField
                              label="SPU"
                              value={row.spuName || row.spu_name || ''}
                              className="spo-return-product__sku"
                            />
                            <HoverCopyField
                              label="SKC"
                              value={row.skcName || ''}
                              className="spo-return-product__sku"
                            />
                            <HoverCopyField
                              label={fixedUi("货号")}
                              value={row.supplierCode || ''}
                              className="spo-return-product__sku"
                            />
                            <div className="spo-return-product__sku">{row.saleAttributeValue || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <HoverCopyField
                          value={row.discussSn}
                          display={row.discussSn ? <strong>{row.discussSn}</strong> : '—'}
                        />
                      </td>
                      <td>{discussTypeLabel(row.discussType)}</td>
                      <td>{quoteText}</td>
                      <td>
                        <span className={`spo-records__status spo-records__status--${discussStatusModifier(row.discussStatus)}`}>
                          {discussStatusLabel(row.discussStatus)}
                        </span>
                      </td>
                      <td>
                        {fixedUiTpl('第 {0} 轮', row.serialNumber ?? '—')}
                        <div className="spo-return-product__sku">{fixedUiTpl('剩余 {0}', row.appealCount ?? '—')}</div>
                      </td>
                      <td>
                        <div className="spo-discuss-suggest-cell">
                          <span>{suggestText}</span>
                          {appealReason ? (
                            <button
                              type="button"
                              className="spo-discuss-appeal-reason"
                              title={appealReason}
                              aria-label={fixedUiTpl('改价原因：{0}', appealReason)}
                              onClick={(e) => {
                                e.stopPropagation();
                                showToast(fixedUiTpl('改价原因：{0}', appealReason), 'info');
                              }}
                            >
                              <CircleHelp size={13} aria-hidden="true" />
                              <span>{fixedUi("改价原因")}</span>
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td className="spo-certs-td spo-certs-td--ops spo-ops-td--ops">
                        <div className="spo-drafts__actions spo-discuss-ops">
                          {canProcess ? (
                            <>
                              <button
                                type="button"
                                className="spo-btn spo-btn--primary spo-btn--sm"
                                disabled={processing || batchBusy}
                                onClick={() => submitConfirmAction(row, 1)}
                              >
                                同意
                              </button>
                              <button
                                type="button"
                                className="spo-btn spo-btn--danger spo-btn--sm"
                                disabled={processing || batchBusy}
                                onClick={() => submitConfirmAction(row, 2)}
                              >
                                拒绝
                              </button>
                              <button
                                type="button"
                                className="spo-btn spo-btn--secondary spo-btn--sm"
                                disabled={processing || batchBusy}
                                onClick={() => openProcessModal(row, 'requote')}
                              >
                                重新报价
                              </button>
                            </>
                          ) : (
                            <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" disabled>
                              不可处理
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <SheinOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </ProductListCard>

      <ProcessDiscussModal
        open={Boolean(processRow)}
        row={processRow}
        busy={processing}
        initialMode={processMode}
        onClose={() => (!processing ? setProcessRow(null) : null)}
        onSubmit={handleProcess}
      />
      <SheinSyncMaxPagesModal
        open={syncModalOpen}
        title={fixedUi("同步议价单")}
        hint=""
        pageSizeHint={200}
        defaultMaxPages={10}
        absMaxPages={100}
        confirming={false}
        onClose={() => {
          if (!syncing) setSyncModalOpen(false);
        }}
        onConfirm={async ({ maxPages }) => {
          setSyncModalOpen(false);
          try {
            await guardedSync({ maxPages });
          } catch (_) {
            // toast 已在 handleSync / SyncJobContext 处理
          }
        }}
      />
      <DiscussAutoRuleModal
        open={autoRuleModalOpen}
        shopId={shopId}
        busy={processing || batchBusy || syncing}
        onClose={() => setAutoRuleModalOpen(false)}
      />
    </>
  );
}
